/**
 * TripGenerationContext — web port.
 *
 * Tracks AI trip generation jobs in-memory, runs a single backoff-aware
 * polling loop, and surfaces completion via toast. Foreground refresh
 * uses `visibilitychange` (web equivalent of RN AppState 'active').
 *
 * NB: The web ToastContext has no `actionLabel`/`onPress` support, so the
 * "View" action from RN is omitted — the user opens the trip from inbox.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { tripPlanningApi } from '../api/tripPlanningApi';
import { useToast } from './ToastContext';
import { useAuth } from '../hooks/useAuth';
import { useNotificationBadges } from './NotificationBadgeContext';
import logger from '../utils/logger';
import type { TripGenerationStatus } from '@wg/types';

interface PendingGeneration {
  requestId: string;
  destination: string;
  status: TripGenerationStatus;
  createdAt: number;
  itineraryId?: string;
}

interface CompletedGeneration extends PendingGeneration {
  completedAt: number;
  itineraryId: string;
}

interface TripGenerationContextValue {
  trackGeneration: (requestId: string, destination: string) => void;
  pendingGenerations: PendingGeneration[];
  completedGenerations: CompletedGeneration[];
  pendingReviewRequestId: string | null;
  pendingReviewItineraryId: string | null;
  setPendingReview: (requestId: string, itineraryId: string) => void;
  clearPendingReview: () => void;
  dismissGeneration: (requestId: string) => void;
  hasActiveGenerations: boolean;
}

const TripGenerationContext = createContext<TripGenerationContextValue>({
  trackGeneration: () => {},
  pendingGenerations: [],
  completedGenerations: [],
  pendingReviewRequestId: null,
  pendingReviewItineraryId: null,
  setPendingReview: () => {},
  clearPendingReview: () => {},
  dismissGeneration: () => {},
  hasActiveGenerations: false,
});

const POLL_INTERVAL_MS = 10000;
const MIN_POLL_GAP_MS = 5000;
const MAX_BACKOFF_MS = 60000;

export const TripGenerationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();
  const { refresh: refreshBadges } = useNotificationBadges();

  const [pendingGenerations, setPendingGenerations] = useState<
    PendingGeneration[]
  >([]);
  const [completedGenerations, setCompletedGenerations] = useState<
    CompletedGeneration[]
  >([]);
  const [pendingReviewRequestId, setPendingReviewRequestId] = useState<
    string | null
  >(null);
  const [pendingReviewItineraryId, setPendingReviewItineraryId] = useState<
    string | null
  >(null);

  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);
  const isMountedRef = useRef(true);
  const lastPollTimeRef = useRef<number>(0);
  const backoffMultiplierRef = useRef<number>(1);
  const pendingGenerationsRef = useRef<PendingGeneration[]>([]);
  pendingGenerationsRef.current = pendingGenerations;

  const pollPendingGenerations = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastPoll = now - lastPollTimeRef.current;
    const currentPending = pendingGenerationsRef.current;

    if (isPollingRef.current) return;
    if (currentPending.length === 0) return;
    if (timeSinceLastPoll < MIN_POLL_GAP_MS) return;

    isPollingRef.current = true;
    lastPollTimeRef.current = now;

    try {
      const updates: PendingGeneration[] = [];
      const newlyCompleted: CompletedGeneration[] = [];
      let hadError = false;

      for (const gen of currentPending) {
        try {
          const result = await tripPlanningApi.getGenerationStatus(
            gen.requestId,
          );

          if (
            result.status === 'generated' ||
            result.status === 'user_review'
          ) {
            newlyCompleted.push({
              ...gen,
              status: result.status,
              itineraryId: result.itinerary_id!,
              completedAt: Date.now(),
            });
          } else if (result.status === 'rejected' || result.error) {
            logger.warn('[TripGenerationContext] Generation failed', {
              requestId: gen.requestId,
              error: result.error,
            });
          } else {
            updates.push({ ...gen, status: result.status });
          }
        } catch (err: any) {
          const status = err?.response?.status;
          const is429 = status === 429 || err?.message?.includes('429');
          const is404 = status === 404;

          if (is404) {
            logger.warn('[TripGenerationContext] Request not found, removing', {
              requestId: gen.requestId,
            });
          } else if (is429) {
            hadError = true;
            backoffMultiplierRef.current = Math.min(
              backoffMultiplierRef.current * 2,
              6,
            );
            updates.push(gen);
          } else {
            hadError = true;
            updates.push(gen);
          }
        }
      }

      if (!isMountedRef.current) return;
      if (!hadError) backoffMultiplierRef.current = 1;

      setPendingGenerations(updates);

      if (newlyCompleted.length > 0) {
        setCompletedGenerations(prev => [...prev, ...newlyCompleted]);
        refreshBadges();

        for (const completed of newlyCompleted) {
          showToast(`Your ${completed.destination} trip is ready!`, {
            variant: 'success',
            duration: 6000,
            actionLabel: 'View',
            onPress: () => {
              setPendingReviewRequestId(completed.requestId);
              setPendingReviewItineraryId(completed.itineraryId);
            },
          });
        }
      }
    } finally {
      isPollingRef.current = false;
    }
  }, [showToast, refreshBadges]);

  useEffect(() => {
    const scheduleNextPoll = () => {
      if (!isMountedRef.current) return;
      if (pendingGenerationsRef.current.length === 0) return;

      const interval = POLL_INTERVAL_MS * backoffMultiplierRef.current;
      const cappedInterval = Math.min(interval, MAX_BACKOFF_MS);

      pollIntervalRef.current = setTimeout(async () => {
        await pollPendingGenerations();
        scheduleNextPoll();
      }, cappedInterval);
    };

    if (pendingGenerations.length > 0 && isAuthenticated) {
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setTimeout(async () => {
          await pollPendingGenerations();
          scheduleNextPoll();
        }, 2000);
      }
    } else if (pollIntervalRef.current) {
      clearTimeout(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    return () => {
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [pendingGenerations.length, isAuthenticated, pollPendingGenerations]);

  useEffect(() => {
    const onVis = () => {
      if (
        document.visibilityState === 'visible' &&
        pendingGenerationsRef.current.length > 0
      ) {
        pollPendingGenerations();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [pollPendingGenerations]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) clearTimeout(pollIntervalRef.current);
    };
  }, []);

  const trackGeneration = useCallback(
    (requestId: string, destination: string) => {
      const newGen: PendingGeneration = {
        requestId,
        destination,
        status: 'pending',
        createdAt: Date.now(),
      };
      setPendingGenerations(prev => {
        if (prev.some(g => g.requestId === requestId)) return prev;
        return [...prev, newGen];
      });
    },
    [],
  );

  const setPendingReview = useCallback(
    (requestId: string, itineraryId: string) => {
      setPendingReviewRequestId(requestId);
      setPendingReviewItineraryId(itineraryId);
    },
    [],
  );

  const clearPendingReview = useCallback(() => {
    setPendingReviewRequestId(null);
    setPendingReviewItineraryId(null);
  }, []);

  const dismissGeneration = useCallback(
    (requestId: string) => {
      setCompletedGenerations(prev =>
        prev.filter(g => g.requestId !== requestId),
      );
      setPendingGenerations(prev =>
        prev.filter(g => g.requestId !== requestId),
      );
      if (pendingReviewRequestId === requestId) clearPendingReview();
    },
    [pendingReviewRequestId, clearPendingReview],
  );

  const value = useMemo<TripGenerationContextValue>(
    () => ({
      trackGeneration,
      pendingGenerations,
      completedGenerations,
      pendingReviewRequestId,
      pendingReviewItineraryId,
      setPendingReview,
      clearPendingReview,
      dismissGeneration,
      hasActiveGenerations: pendingGenerations.length > 0,
    }),
    [
      trackGeneration,
      pendingGenerations,
      completedGenerations,
      pendingReviewRequestId,
      pendingReviewItineraryId,
      setPendingReview,
      clearPendingReview,
      dismissGeneration,
    ],
  );

  return (
    <TripGenerationContext.Provider value={value}>
      {children}
    </TripGenerationContext.Provider>
  );
};

export const useTripGeneration = () => useContext(TripGenerationContext);
export default TripGenerationContext;
